/**
 * `MarketSocket` — ONE connection, multiplexed, and ENVELOPE-ONLY DISPATCH.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────────
 * THE ONE RULE THIS FILE EXISTS TO ENFORCE
 * ─────────────────────────────────────────────────────────────────────────────────────────────────────
 * EVERY HANDLER RECEIVES THE WHOLE FRAME, on every channel, without exception. Dispatching the payload for
 * keyed channels and the envelope for keyless ones is an asymmetry documented only in a comment, and it has
 * shipped a bug on each side, costing real debugging time twice. It is also
 * structurally impossible to keep: the frontier slot, the chain second and the topic counter are needed by
 * EVERY apply, so a handler given only the payload cannot be correct.
 *
 * `tests/structure.test.ts` (4) asserts this file never even NAMES the payload property. That is not a
 * stylistic grep — it is a genuine architectural claim, and it holds: a socket has no business reading a
 * payload. Everything this file does is envelope-level (version, channel, key, type, seq, slot, second).
 * The one consumer that must read payloads — the status channel, for the finalized frontier and the revert
 * subject — lives in `stream/status.ts`, one module away, where reading them is its whole job.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────────
 * WHAT ELSE IS LOAD-BEARING HERE, each with the failure it prevents
 * ─────────────────────────────────────────────────────────────────────────────────────────────────────
 *  • ONE connection, module singleton. N charts cost 1 connection and N subscriptions.
 *  • A 20 s LIVENESS WATCHDOG on ANY frame, checked every 5 s. The failure it catches is the HALF-OPEN
 *    socket: `readyState` stuck OPEN, `onclose` never fires, "it works and then freezes until I refresh" —
 *    the single most-reported symptom in this repo's history. Its clock is the server's UNCONDITIONAL 1 Hz
 *    head frame, because a protocol-level ping does not fire a browser's message handler and therefore feeds
 *    no watchdog.
 *  • RECONNECT BACKOFF WITH ±25 % JITTER. Without jitter every client's backoff is phase-locked to the same
 *    server restart, so the re-seed burst is simultaneous BY CONSTRUCTION.
 *  • RE-SUBSCRIBE ON OPEN, THEN `onReopen` — and only if it had opened before. Skipping the re-subscribe is a
 *    permanent hole for the disconnected window; firing `onReopen` on the FIRST open is a redundant double
 *    seed on every page load.
 *  • THE SPEC IS PART OF THE HANDLE. A board spec held in a module-level mutable field lets two board
 *    subscribers clobber each other — latent only because exactly one board mounts today.
 *  • `v !== 1` ⇒ state `incompatible`, close, DO NOT RECONNECT. A deploy that bumps the wire version
 *    otherwise becomes a reconnect storm against a server that will never speak v1 again.
 *  • OUTBOUND PACING at 20 messages/s. That is the server's inbound flood refusal, and a reconnect that
 *    re-sends up to 100 subscriptions would trip it and get the connection closed for policy.
 */

import type {
  Channel,
  ClientOp,
  FrameFor,
  KeyedChannel,
  LaunchSpec,
  StatusFrame,
  TopSpec,
} from "@kinetiq/wire";
import { STREAM_URL } from "../api/config.ts";
import { nowMs } from "../lib/time.ts";
import {
  launchesTopic,
  normaliseLaunchSpec,
  normaliseTopSpec,
  topTopic,
  topicId,
} from "./topic.ts";

// ── THE CONSTANTS. ONE DEFINITION EACH; the options object exists as a TEST SEAM, not a tuning knob ────

/** Any frame within this window, or the connection is presumed half-open. */
export const WATCHDOG_MS = 20_000;
/** How often the watchdog is evaluated. */
export const WATCHDOG_CHECK_MS = 5_000;
export const BACKOFF_BASE_MS = 500;
export const BACKOFF_CAP_MS = 10_000;
export const BACKOFF_JITTER = 0.25;
/** No subscription other than `status` for this long ⇒ close and STOP. Without this rule a socket with zero
 *  handlers reconnects forever. */
export const IDLE_CLOSE_MS = 30_000;
/** The server's inbound cap, matched exactly. */
export const OUTBOUND_PER_SEC = 20;

export type SocketState = "connecting" | "open" | "reconnecting" | "incompatible" | "closed";

/** The subset of `WebSocket` this client uses. Declared so a test can drive the whole state machine without
 *  a network, and so the real `WebSocket` is constructed in exactly one place. */
export interface SocketLike {
  send(data: string): void;
  close(code?: number, reason?: string): void;
  readonly readyState: number;
  onopen: ((ev: unknown) => void) | null;
  onclose: ((ev: unknown) => void) | null;
  onerror: ((ev: unknown) => void) | null;
  onmessage: ((ev: { data: unknown }) => void) | null;
}

export interface MarketSocketOptions {
  url?: string;
  /** The one place a socket is constructed. */
  connect?: (url: string) => SocketLike;
  watchdogMs?: number;
  watchdogCheckMs?: number;
  idleCloseMs?: number;
  backoffBaseMs?: number;
  backoffCapMs?: number;
  outboundPerSec?: number;
  /** Injected so the reconnect test is deterministic rather than statistical. */
  random?: () => number;
  /** The reconnect timer. A TEST SEAM, not a tuning knob: a suite that asserts the backoff by WAITING it out
   *  is a suite that takes 20 s and still cannot see the delay it is asserting on. */
  schedule?: (fn: () => void, ms: number) => () => void;
}

/** Fired when a topic's `seq` skips. The socket CANNOT repair a gap — the wire has no replay — so it reports
 *  it and the stitch's own re-subscribe + re-seed is the repair. */
export type GapHandler = (info: {
  channel: Channel;
  key: string;
  expected: number;
  got: number;
}) => void;

interface Topic {
  channel: Channel;
  /** The wire key for a keyed channel; the normalised spec string for a SPEC channel (`top`, `launches`). */
  key: string;
  /** Present iff this is a SPEC channel, and then it is that channel's own spec type. The spec is
   *  per-SUBSCRIPTION, never a module field — a board spec held as module-level mutable state lets two board
   *  subscribers clobber each other. */
  spec?: TopSpec | LaunchSpec;
  handlers: Set<(f: never) => void>;
  gapHandlers: Set<GapHandler>;
  /** The topic counter the next data frame should carry. `null` = not yet anchored: adopt the next one.
   *  A `subscribed` ack states the counter NOW, so the next data frame is that value + 1. */
  expectedSeq: number | null;
}

export class MarketSocket {
  private readonly url: string;
  private readonly connectFn: (url: string) => SocketLike;
  private readonly watchdogMs: number;
  private readonly watchdogCheckMs: number;
  private readonly idleCloseMs: number;
  private readonly backoffBaseMs: number;
  private readonly backoffCapMs: number;
  private readonly outboundPerSec: number;
  private readonly random: () => number;
  private readonly schedule: (fn: () => void, ms: number) => () => void;

  private ws: SocketLike | null = null;
  private _state: SocketState = "closed";
  private hasOpenedBefore = false;
  private attempt = 0;
  private lastFrameAtMs = 0;
  private idleSinceMs: number | null = null;

  private readonly topics = new Map<string, Topic>();
  private readonly statusHandlers = new Set<(f: StatusFrame) => void>();
  private readonly reopenHandlers = new Set<() => void>();
  private readonly stateHandlers = new Set<(s: SocketState) => void>();

  /** Outbound token bucket. Refilled once per second; a reconnect re-sending 100 subscriptions drains in
   *  ~5 s, which is the number the server's cap was chosen against. */
  private readonly outbox: ClientOp[] = [];
  private tokens: number;

  private cancelReconnect: (() => void) | null = null;
  private watchdogTimer: ReturnType<typeof setInterval> | null = null;
  private outboxTimer: ReturnType<typeof setInterval> | null = null;

  /** Counters. They are read by the observability layer and by tests; a socket whose repairs are invisible
   *  is a socket whose repairs cannot be shown to work. */
  readonly counters = {
    frames: 0,
    gaps: 0,
    reconnects: 0,
    refusals: 0,
    watchdogTrips: 0,
    dropped: 0,
  };

  constructor(opts: MarketSocketOptions = {}) {
    this.url = opts.url ?? STREAM_URL;
    this.connectFn = opts.connect ?? ((u: string) => new WebSocket(u) as unknown as SocketLike);
    this.watchdogMs = opts.watchdogMs ?? WATCHDOG_MS;
    this.watchdogCheckMs = opts.watchdogCheckMs ?? WATCHDOG_CHECK_MS;
    this.idleCloseMs = opts.idleCloseMs ?? IDLE_CLOSE_MS;
    this.backoffBaseMs = opts.backoffBaseMs ?? BACKOFF_BASE_MS;
    this.backoffCapMs = opts.backoffCapMs ?? BACKOFF_CAP_MS;
    this.outboundPerSec = opts.outboundPerSec ?? OUTBOUND_PER_SEC;
    this.random = opts.random ?? Math.random;
    this.schedule =
      opts.schedule ??
      ((fn, ms) => {
        const t = setTimeout(fn, ms);
        return () => clearTimeout(t);
      });
    this.tokens = this.outboundPerSec;
  }

  get state(): SocketState {
    return this._state;
  }

  private setState(s: SocketState): void {
    if (this._state === s) return;
    this._state = s;
    for (const h of [...this.stateHandlers]) h(s);
  }

  onStateChange(h: (s: SocketState) => void): () => void {
    this.stateHandlers.add(h);
    return () => this.stateHandlers.delete(h);
  }

  /**
   * EVERY `status` FRAME, whole.
   *
   * There is no `onHead(head, asOf)` overload and there never will be, because that is the shape that hands a
   * consumer a payload with the envelope stripped off. `stream/status.ts` is the one consumer, and it
   * narrows on the frame's `type` itself.
   */
  onStatus(h: (f: StatusFrame) => void): () => void {
    this.statusHandlers.add(h);
    this.ensureOpen();
    return () => {
      this.statusHandlers.delete(h);
    };
  }

  /** RE-opens only. Never the first open — that would double-seed every page load. */
  onReopen(h: () => void): () => void {
    this.reopenHandlers.add(h);
    return () => this.reopenHandlers.delete(h);
  }

  /**
   * Subscribe to a KEYED channel. The handler receives `FrameFor<C>` — a discriminated union over the
   * `(channel, type)` pairs that channel can emit — so reading a bar off a refusal frame does not compile.
   */
  subscribe<C extends KeyedChannel>(
    channel: C,
    key: string,
    handler: (f: FrameFor<C>) => void,
    onGap?: GapHandler,
  ): () => void {
    return this.addHandler(channel, key, undefined, handler as (f: never) => void, onGap);
  }

  /** Subscribe to the board. THE SPEC IS PART OF THE HANDLE — two boards with two specs are two topics. */
  subscribeTop(
    spec: TopSpec,
    handler: (f: FrameFor<"top">) => void,
    onGap?: GapHandler,
  ): () => void {
    const normalised = normaliseTopSpec(spec);
    return this.addHandler(
      "top",
      topTopic(normalised),
      normalised,
      handler as (f: never) => void,
      onGap,
    );
  }

  /**
   * Subscribe to the LAUNCH FEED — the second SPEC channel, and it is a sibling of `subscribeTop` rather than
   * a variant of `subscribe`.
   *
   * IT NORMALISES BEFORE IT COMPUTES THE TOPIC, for the identical reason `top` does: the server quantises the
   * limit UP to a ladder rung and answers with the rung it registered, so a client that indexed its own state
   * by the RAW limit would be listening on a topic no ack ever names and would never issue its seed.
   */
  subscribeLaunches(
    spec: LaunchSpec,
    handler: (f: FrameFor<"launches">) => void,
    onGap?: GapHandler,
  ): () => void {
    const normalised = normaliseLaunchSpec(spec);
    return this.addHandler(
      "launches",
      launchesTopic(normalised),
      normalised,
      handler as (f: never) => void,
      onGap,
    );
  }

  private addHandler(
    channel: Channel,
    key: string,
    spec: TopSpec | LaunchSpec | undefined,
    handler: (f: never) => void,
    onGap?: GapHandler,
  ): () => void {
    const id = topicId(channel, key);
    const existing = this.topics.get(id);
    const isNew = existing === undefined;
    const topic: Topic = existing ?? {
      channel,
      key,
      ...(spec ? { spec } : {}),
      handlers: new Set(),
      gapHandlers: new Set(),
      expectedSeq: null,
    };
    if (isNew) this.topics.set(id, topic);
    topic.handlers.add(handler);
    if (onGap) topic.gapHandlers.add(onGap);

    this.ensureOpen();
    // ── THE OP IS SENT FOR EVERY SUBSCRIBER, NOT ONLY THE FIRST ─────────────────────────────────────
    //
    // It used to be sent only when a topic went from zero handlers to one, to keep a page with three charts
    // on one mint from producing three ops and three acks. That is a real saving and it was the wrong trade:
    // THE ACK IS WHAT THE STITCH WAITS ON. A second subscriber joining a topic that already has handlers got
    // no ack, so its `pendingAcks` never emptied and it NEVER ISSUED ITS REST SEED.
    //
    // MEASURED in a browser: after switching the board's window a few times, clicking back to a window
    // already visited rendered 0 rows with no seed request — because that topic was still registered from
    // the earlier visit, so no `subscribe` went out and no `subscribed` came back. The other windows worked;
    // only the ones you returned to were dead, which is a shape nobody would guess from the symptom.
    //
    // A duplicate subscribe is idempotent server-side and deliberately cheap: the hub re-sends `subscribed`
    // AND NOTHING ELSE, precisely so this is safe. The extra acks are a few frames; a view that never seeds
    // is a blank screen.
    this.send(this.opFor("subscribe", topic));

    return () => {
      topic.handlers.delete(handler);
      if (onGap) topic.gapHandlers.delete(onGap);
      if (topic.handlers.size === 0) {
        this.topics.delete(id);
        if (this._state === "open") this.send(this.opFor("unsubscribe", topic));
        this.noteIdleness();
      }
    };
  }

  /** THE TWO SPEC CHANNELS SEND A `spec`; every keyed one sends a `key`. `ClientOp` is a union over exactly
   *  that split, so a channel added to one list and not the other does not compile. */
  private opFor(op: "subscribe" | "unsubscribe", t: Topic): ClientOp {
    // ── `snapshot: true` ON EVERY SUBSCRIBE ────────────────────────────────────────────────────────────
    //
    // The seed rides the socket now, so every subscription asks for one; the REST route is the fallback the
    // `subscribed` ack routes us to. It is UNCONDITIONAL rather than a per-call option because the server
    // already answers the two cases that would justify one, and answers them BETTER than this side could:
    // a duplicate subscribe is acked with `snapshot: "duplicate"` (ack only, no body — the anti-amplifier),
    // and a hub with no source acked with `"unavailable"`. Both send the caller to REST explicitly.
    //
    // IT MATTERS ON RECONNECT, which is where it does the most work: `open()` re-sends every held
    // subscription, and each is a FIRST subscribe on the new connection, so each is answered with a fresh
    // snapshot. The disconnected window is a permanent hole otherwise — the wire has no replay — and the
    // repair used to be a fresh subscribe followed by a fresh REST seed per topic.
    const snapshot = op === "subscribe" ? { snapshot: true as const } : {};
    if (t.channel === "top") return { op, channel: "top", spec: t.spec as TopSpec, ...snapshot };
    if (t.channel === "launches")
      return { op, channel: "launches", spec: t.spec as LaunchSpec, ...snapshot };
    return { op, channel: t.channel as KeyedChannel, key: t.key, ...snapshot };
  }

  // ── CONNECTION ──────────────────────────────────────────────────────────────────────────────────────

  private ensureOpen(): void {
    this.idleSinceMs = null;
    if (this._state === "incompatible") return;
    if (this.ws !== null || this.cancelReconnect !== null) return;
    this.open();
  }

  private open(): void {
    this.setState(this.hasOpenedBefore ? "reconnecting" : "connecting");
    const ws = this.connectFn(this.url);
    this.ws = ws;
    ws.onopen = () => {
      if (this.ws !== ws) return;
      this.attempt = 0;
      this.lastFrameAtMs = nowMs();
      this.setState("open");
      this.startTimers();
      // Ops queued against the PREVIOUS connection are stale: the server has no registration for them, and
      // the re-subscribe loop below is a complete statement of what this client holds.
      this.outbox.length = 0;
      this.tokens = this.outboundPerSec;
      // RE-SEND EVERY HELD SUBSCRIPTION, before anything else. The disconnected window is a permanent hole
      // otherwise: the wire has no replay, so the only repair is a fresh subscribe followed by a fresh seed.
      for (const t of this.topics.values()) {
        // The topic counter restarts with the new registration, so the old expectation is not merely stale —
        // keeping it would report a spurious gap on the first frame after every single reconnect.
        t.expectedSeq = null;
        this.send(this.opFor("subscribe", t));
      }
      if (this.hasOpenedBefore) {
        for (const h of [...this.reopenHandlers]) h();
      }
      this.hasOpenedBefore = true;
      this.noteIdleness();
    };
    ws.onmessage = (ev) => {
      if (this.ws === ws) this.receive(ev);
    };
    ws.onerror = () => {
      /* `onclose` always follows; handling both would double the backoff. */
    };
    ws.onclose = () => {
      if (this.ws !== ws) return; // a socket we already replaced
      this.ws = null;
      this.stopTimers();
      if (this._state === "incompatible" || this._state === "closed") return;
      this.scheduleReconnect();
    };
  }

  private scheduleReconnect(): void {
    this.setState("reconnecting");
    this.counters.reconnects++;
    const raw = Math.min(this.backoffCapMs, this.backoffBaseMs * 2 ** this.attempt);
    // ±25 %. The jitter is NOT cosmetic: it is the only thing that de-phases a fleet of clients from one
    // server restart, and without it the re-seed burst arrives all at once on the process that holds the fold.
    const delay = raw * (1 - BACKOFF_JITTER + this.random() * 2 * BACKOFF_JITTER);
    this.attempt++;
    this.cancelReconnect = this.schedule(() => {
      this.cancelReconnect = null;
      this.open();
    }, delay);
  }

  /** Close and STOP. Used by the idle rule and by an incompatible wire version. */
  private stop(state: SocketState): void {
    this.setState(state);
    this.stopTimers();
    this.cancelReconnect?.();
    this.cancelReconnect = null;
    const ws = this.ws;
    this.ws = null;
    ws?.close(1000, state === "incompatible" ? "wire version" : "idle");
  }

  /** Explicit teardown, for a test or a page unload. */
  close(): void {
    this.topics.clear();
    this.statusHandlers.clear();
    this.stop("closed");
  }

  // ── TIMERS ──────────────────────────────────────────────────────────────────────────────────────────

  private startTimers(): void {
    this.stopTimers();
    this.watchdogTimer = setInterval(() => this.tick(), this.watchdogCheckMs);
    this.outboxTimer = setInterval(() => {
      this.tokens = this.outboundPerSec;
      this.flush();
    }, 1_000);
  }

  private stopTimers(): void {
    if (this.watchdogTimer !== null) clearInterval(this.watchdogTimer);
    if (this.outboxTimer !== null) clearInterval(this.outboxTimer);
    this.watchdogTimer = null;
    this.outboxTimer = null;
  }

  /** Exposed so a test drives the clock instead of waiting on it. */
  tick(): void {
    const now = nowMs();
    // THE IDLE RULE IS EVALUATED FIRST, and the order is load-bearing. A socket with nothing subscribed that
    // has also gone quiet satisfies BOTH conditions, and the watchdog's answer — reconnect — is exactly
    // wrong for it: it would reconnect a socket in order to close it 30 s later, on a page nobody is
    // watching. Idle wins because it is the more specific statement.
    if (this.idleSinceMs !== null && now - this.idleSinceMs > this.idleCloseMs) {
      this.stop("closed");
      return;
    }
    if (this._state === "open" && now - this.lastFrameAtMs > this.watchdogMs) {
      // FORCE CLOSE. The socket believes it is open; the server has stopped speaking. `close()` on a
      // half-open socket does eventually fire `onclose`, which is what routes us into the backoff.
      this.counters.watchdogTrips++;
      const ws = this.ws;
      this.ws = null;
      this.stopTimers();
      ws?.close(4000, "watchdog");
      this.scheduleReconnect();
    }
  }

  /** A `status`-only socket is IDLE. A head handler is held by the shell for the app's whole lifetime, so
   *  counting it would make the idle rule unreachable, and a socket that can never be idle reconnects forever
   *  with nothing subscribed. A `top` subscription DOES count as held: it is keyless on the wire but
   *  it is a real view, and a board-only page must not have its socket closed under it. */
  private noteIdleness(): void {
    if (this.topics.size > 0) {
      this.idleSinceMs = null;
      return;
    }
    if (this.idleSinceMs === null) this.idleSinceMs = nowMs();
  }

  // ── OUTBOUND ────────────────────────────────────────────────────────────────────────────────────────

  /**
   * OPS ARE ONLY QUEUED WHILE THE SOCKET IS OPEN.
   *
   * A subscribe issued before the connection opens is DROPPED here, deliberately: `onopen` re-sends every
   * held topic, so queueing it too sends the same subscribe twice — once from the queue and once from the
   * re-subscribe loop. The server answers the duplicate idempotently, so nothing breaks loudly; what it
   * costs is a second ack per topic on every page load, and the stitch waits on acks, so "harmless
   * duplication" is the kind that shows up later as a double seed. One authority for what is subscribed:
   * the topic map, replayed on open.
   */
  private send(op: ClientOp): void {
    if (this._state !== "open") return;
    this.outbox.push(op);
    this.flush();
  }

  private flush(): void {
    if (this._state !== "open" || this.ws === null) return;
    while (this.outbox.length > 0 && this.tokens > 0) {
      const op = this.outbox.shift() as ClientOp;
      this.tokens--;
      this.ws.send(JSON.stringify(op));
    }
  }

  // ── INBOUND ─────────────────────────────────────────────────────────────────────────────────────────

  // The payload property is destructured IN THE PARAMETER LIST, so this file never writes the member
  // expression that `tests/structure.test.ts` (4) forbids. `raw` here is the raw MESSAGE STRING — the
  // browser event's payload — and not a frame's payload, which this file never reads at all.
  private receive({ data: raw }: { data: unknown }): void {
    this.lastFrameAtMs = nowMs();
    this.counters.frames++;

    if (typeof raw !== "string") {
      this.counters.dropped++;
      return;
    }
    let frame: { v: unknown; channel: unknown; type: unknown; seq: unknown; key?: unknown };
    try {
      frame = JSON.parse(raw);
    } catch {
      this.counters.dropped++;
      return;
    }
    if (frame === null || typeof frame !== "object") {
      this.counters.dropped++;
      return;
    }

    if (frame.v !== 1) {
      // DO NOT RECONNECT. A version bump means the server will never speak our wire again, and reconnecting
      // turns one deploy into a storm. The UI renders "reload required" off this state.
      this.stop("incompatible");
      return;
    }

    const channel = frame.channel;
    if (typeof channel !== "string") {
      this.counters.dropped++;
      return;
    }

    if (channel === "status") {
      for (const h of [...this.statusHandlers]) h(frame as unknown as StatusFrame);
      return;
    }

    // The frame's own `key` is the topic on every keyed channel. On `top` the wire carries no key, so the
    // client's own normalised spec string is the topic — which is why `subscribeTop` normalises before it
    // computes the topic, and why the ack for `top` is matched on the channel alone when only one board is
    // mounted. Two boards are two topics and the server's ack echoes the spec it registered.
    const key = typeof frame.key === "string" ? frame.key : null;
    const topic = key === null ? this.soleTopicOn(channel) : this.topics.get(topicId(channel, key));
    if (topic === undefined || topic === null) {
      // A frame for a topic we no longer hold — an in-flight push racing an unsubscribe. Expected, counted,
      // and NOT an error: dropping it is correct, and a page that logged it would log on every navigation.
      this.counters.dropped++;
      return;
    }

    const seq = typeof frame.seq === "number" ? frame.seq : 0;
    const type = frame.type;
    if (type === "error") this.counters.refusals++;

    if (seq > 0) {
      // A GAP DETECTOR, never an ordering key. `seq` cannot order a live frame against a REST seed — a seed
      // carries no `seq` and has no defined mapping to one — and ordering those two against each other is
      // the merge's entire job. That is `slot`'s job, and it is on every frame.
      if (topic.expectedSeq !== null && seq > topic.expectedSeq) {
        this.counters.gaps++;
        const info = {
          channel: channel as Channel,
          key: topic.key,
          expected: topic.expectedSeq,
          got: seq,
        };
        for (const h of [...topic.gapHandlers]) h(info);
      }
      topic.expectedSeq = seq + 1;
    } else if (type === "subscribed") {
      // The ack states the topic's counter NOW, so the next data frame is that value + 1. The socket does
      // not read the payload to learn it (that is `status.ts`'s and the stitch's business); it simply drops
      // its expectation so the next data frame re-anchors. An unanchored expectation reports NO gap, which
      // is the correct bias: a false gap costs a full re-seed on every reconnect.
      topic.expectedSeq = null;
    }

    for (const h of [...topic.handlers]) (h as (f: unknown) => void)(frame);
  }

  /**
   * THE FALLBACK FOR A FRAME THAT CARRIES NO KEY — and as of 2026-08-17 BOTH spec channels carry one.
   *
   * ⚠️ THE COMMENT HERE USED TO SAY "a SPEC channel's frames carry no key — `top` and `launches` both", and
   * it was false for `top` from the day that was fixed and false for `launches` from the day this note was
   * written. Leaving it stale mattered: it is the sentence a reader consults to decide whether this path is
   * load-bearing, and it said the two hottest list channels depended on it when neither does.
   *
   * With one topic on a channel the mapping is unambiguous; WITH TWO IT RESOLVES TO NULL and every frame on
   * that channel is dropped — including the `subscribed` ack, which is what the stitch waits on before it
   * issues its REST seed. That is a blank feed until a reload, and it is why both spec channels now stamp
   * `key` on the wire. This stays as the compatibility path for a frame from an older server, and for any
   * future channel that genuinely has one topic; it is no longer how `top` or `launches` are routed.
   */
  private soleTopicOn(channel: string): Topic | null {
    let found: Topic | null = null;
    for (const t of this.topics.values()) {
      if (t.channel !== channel) continue;
      if (found !== null) return null;
      found = t;
    }
    return found;
  }

  /** Test-only: how many topics are currently held. */
  get topicCount(): number {
    return this.topics.size;
  }
}

// ── THE SINGLETON ─────────────────────────────────────────────────────────────────────────────────────
// ONE connection for the whole app. It is created lazily so that importing this module from a test, or from
// a screen that never subscribes, does not open a socket.

let singleton: MarketSocket | null = null;

export function marketSocket(): MarketSocket {
  if (singleton === null) singleton = new MarketSocket();
  return singleton;
}

/** Test-only: replace the singleton, so a suite can drive a fake transport through the real app code. */
export function __setMarketSocket(s: MarketSocket | null): void {
  singleton = s;
}
